import React, { useState } from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';

import type { BatchMergeJobStatusResponse } from '../../types';

interface AutoMergeResultsAlertProps {
  result: BatchMergeJobStatusResponse;
  onDone: () => void;
}

export const AutoMergeResultsAlert: React.FC<AutoMergeResultsAlertProps> = ({ result, onDone }) => {
  const [showFailures, setShowFailures] = useState(false);

  const failedResults = (result.results ?? []).filter(r => !r.success);
  const hasFailures = failedResults.length > 0;
  const allFailed = (result.successful ?? 0) === 0 && hasFailures;

  const severity = allFailed ? 'error' : hasFailures ? 'warning' : 'success';
  const title = allFailed
    ? 'Auto-Merge Failed'
    : hasFailures
      ? 'Auto-Merge Partially Complete'
      : 'Auto-Merge Complete';

  return (
    <Box>
      <Alert
        severity={severity}
        action={
          <Button color="inherit" size="small" onClick={onDone} sx={{ textTransform: 'none' }}>
            Done
          </Button>
        }
        sx={{ mb: hasFailures ? 1 : 0 }}
      >
        <AlertTitle>{title}</AlertTitle>
        <Typography variant="body2">
          {result.successful ?? 0} group{(result.successful ?? 0) !== 1 ? 's' : ''} merged
          successfully
          {hasFailures && (
            <>
              , {result.failed ?? 0} failed
            </>
          )}
        </Typography>
      </Alert>

      {hasFailures && (
        <>
          <Button
            size="small"
            onClick={() => setShowFailures(prev => !prev)}
            endIcon={showFailures ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ textTransform: 'none', mb: 1 }}
          >
            {showFailures ? 'Hide' : 'Show'} failed groups ({failedResults.length})
          </Button>
          <Collapse in={showFailures}>
            <List dense disablePadding>
              {failedResults.map(r => (
                <ListItem key={r.group_id} sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={`Group ${r.group_id.slice(0, 8)}...`}
                    secondary={r.error_message ?? 'Unknown error'}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                    secondaryTypographyProps={{ variant: 'caption', color: 'error' }}
                  />
                </ListItem>
              ))}
            </List>
          </Collapse>
        </>
      )}
    </Box>
  );
};
